import { Select, Text } from '@chakra-ui/react';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconCrossSmall } from 'central-icons/IconCrossSmall';
import type { GroupBase, SingleValue } from 'react-select';

import type { SecondaryDimensionValues } from '@revelio/core';
import { FormatType } from '@revelio/replots';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  IconButton,
  Select as RevelioSelect,
} from '@revelio/ui';

import { DragHandleIcon } from '../../../../shared/ui/icons';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import { secondaryDimensionOptions } from '../PageConfigProvider/types/dimension';
import type {
  ColumnAlignment,
  PositionedItem,
  TableColumnConfig,
} from '../PageConfigProvider/types/item';
import type { MetricValues } from '../PageConfigProvider/types/metric';
import labelStyles from './section-label.module.css';
import styles from './TableColumnsEditor.module.css';
import layoutStyles from './table-columns-editor.module.css';
import { getAvailableMetrics } from './utils/metric-mapping';
import {
  getColumnDimensionLabel,
  getPrimaryDimension,
} from './utils/table-columns';

/** Default format applied to variable columns when none is configured. */
const DEFAULT_VARIABLE_FORMAT = FormatType.DECIMAL;

const FORMAT_OPTIONS: { value: FormatType; label: string }[] = [
  { value: FormatType.SI, label: 'Compact (1.2K)' },
  { value: FormatType.INTEGER, label: 'Integer (1,234)' },
  { value: FormatType.DECIMAL, label: 'Decimal (1,234.5)' },
  { value: FormatType.RATIO, label: 'Ratio (1:50)' },
  { value: FormatType.PERCENTAGE, label: 'Percent (12%)' },
  { value: FormatType.CURRENCY, label: 'Currency ($1.2K)' },
];

const ALIGNMENT_OPTIONS: { value: ColumnAlignment; label: string }[] = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

const DIMENSION_GROUP_LABEL = 'Dimension';

type DimensionOption = {
  value: SecondaryDimensionValues;
  label: string;
  kind: 'dimension';
};

type MetricOption = {
  value: MetricValues;
  label: string;
  kind: 'variable';
};

type ColumnSelectOption = DimensionOption | MetricOption;

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Text
    className={labelStyles.sectionLabel}
    fontSize="xs"
    fontWeight={600}
    mb="0.25rem"
    mt="0.75rem"
  >
    {children}
  </Text>
);

type SortableColumnItemProps = {
  column: TableColumnConfig;
  isDisabled: boolean;
  columnSelectGroups: GroupBase<ColumnSelectOption>[];
  selectedOption: (column: TableColumnConfig) => ColumnSelectOption | null;
  onSelect: (column: TableColumnConfig, option: ColumnSelectOption) => void;
  onUpdate: (column: TableColumnConfig) => void;
  onRemove: (columnId: string) => void;
  headerLabel: (column: TableColumnConfig) => string;
};

/** A column row in the config panel, draggable to reorder via its handle. */
const SortableColumnItem = ({
  column,
  isDisabled,
  columnSelectGroups,
  selectedOption,
  onSelect,
  onUpdate,
  onRemove,
  headerLabel,
}: SortableColumnItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <AccordionItem
      ref={setNodeRef}
      value={column.id}
      style={style}
      className={layoutStyles.columnCard}
      data-dragging={isDragging || undefined}
    >
      <div className={layoutStyles.columnItem}>
        <IconButton
          aria-label="Drag to reorder column"
          icon={<DragHandleIcon size="0.625rem" />}
          size="sm"
          variant="ghost"
          shape="rounded"
          className={styles.dragHandle}
          disabled={isDisabled}
          {...attributes}
          {...listeners}
        />
        <AccordionButton className={layoutStyles.columnToggle}>
          <div className={layoutStyles.columnLabel}>{headerLabel(column)}</div>
          <AccordionIcon />
        </AccordionButton>
        <IconButton
          aria-label="Remove column"
          icon={<IconCrossSmall size={16} />}
          size="sm"
          variant="ghost"
          shape="rounded"
          disabled={isDisabled}
          onClick={() => onRemove(column.id)}
        />
      </div>

      <AccordionPanel className={layoutStyles.columnPanel}>
        <SectionLabel>Column</SectionLabel>
        <RevelioSelect<ColumnSelectOption, false, GroupBase<ColumnSelectOption>>
          size="sm"
          options={columnSelectGroups}
          value={selectedOption(column)}
          isDisabled={isDisabled}
          isSearchable
          isClearable={false}
          onChange={(opt: SingleValue<ColumnSelectOption>) => {
            if (!opt) return;
            onSelect(column, opt);
          }}
        />

        {column.kind === 'variable' ? (
          <>
            <SectionLabel>Format</SectionLabel>
            <Select
              size="sm"
              value={column.format ?? DEFAULT_VARIABLE_FORMAT}
              isDisabled={isDisabled}
              onChange={(e) =>
                onUpdate({
                  ...column,
                  format: e.target.value as FormatType,
                })
              }
            >
              {FORMAT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </>
        ) : (
          <>
            <SectionLabel>Alignment</SectionLabel>
            <Select
              size="sm"
              value={column.alignment}
              isDisabled={isDisabled}
              onChange={(e) =>
                onUpdate({
                  ...column,
                  alignment: e.target.value as ColumnAlignment,
                })
              }
            >
              {ALIGNMENT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </>
        )}
      </AccordionPanel>
    </AccordionItem>
  );
};

export const TableColumnsEditor = ({
  item,
  isDisabled = false,
}: {
  item: PositionedItem;
  isDisabled?: boolean;
}) => {
  const { dispatch, primaryFilter } = useTabContentConfig();
  const primaryFilterId = primaryFilter?.id;
  const id = item.id;
  const columns = item.plot?.tableColumns ?? [];

  const metricGroups =
    getAvailableMetrics({ primaryFilterId, chartType: 'table' }) ?? [];
  const flatMetricOptions = metricGroups.flatMap((group) => group.options);
  const metricLabel = (metric: MetricValues): string =>
    flatMetricOptions.find((opt) => opt.value === metric)?.label ?? metric;

  // Surface the page's primary-filter dimension first; keep the rest in order.
  const primaryDimension = primaryFilterId
    ? getPrimaryDimension(primaryFilterId)
    : undefined;
  const sortedDimensionOptions = secondaryDimensionOptions
    .filter((opt) => opt.value !== 'activities' && opt.value !== 'subsidiaries')
    .sort((a, b) => {
      if (a.value === primaryDimension) return -1;
      if (b.value === primaryDimension) return 1;
      return 0;
    });

  const dimensionGroup: GroupBase<DimensionOption> = {
    label: DIMENSION_GROUP_LABEL,
    options: sortedDimensionOptions.map(
      (opt): DimensionOption => ({
        value: opt.value,
        label: getColumnDimensionLabel(opt.value),
        kind: 'dimension',
      })
    ),
  };

  const metricGroupOptions: GroupBase<MetricOption>[] = metricGroups.map(
    (group) => ({
      label: group.label,
      options: group.options.map(
        (opt): MetricOption => ({
          value: opt.value,
          label: opt.label,
          kind: 'variable',
        })
      ),
    })
  );

  const columnSelectGroups = [dimensionGroup, ...metricGroupOptions];

  const addColumn = (option: ColumnSelectOption) => {
    const column: TableColumnConfig =
      option.kind === 'dimension'
        ? {
            id: crypto.randomUUID(),
            kind: 'dimension',
            dimension: option.value,
            alignment: 'left',
            label: getColumnDimensionLabel(option.value),
          }
        : {
            id: crypto.randomUUID(),
            kind: 'variable',
            metric: option.value,
            aggregation: 'sum',
            label: metricLabel(option.value),
          };
    dispatch({ type: 'ADD_TABLE_COLUMN', payload: { id, column } });
  };

  const updateColumn = (column: TableColumnConfig) =>
    dispatch({ type: 'UPDATE_TABLE_COLUMN', payload: { id, column } });

  const removeColumn = (columnId: string) =>
    dispatch({ type: 'REMOVE_TABLE_COLUMN', payload: { id, columnId } });

  const handleSelect = (
    column: TableColumnConfig,
    option: ColumnSelectOption
  ) => {
    if (option.kind === 'dimension') {
      updateColumn({
        ...(column.kind === 'dimension' ? column : {}),
        id: column.id,
        kind: option.kind,
        dimension: option.value,
        label: option.label,
      });
    } else {
      updateColumn({
        ...(column.kind === 'variable' ? column : {}),
        id: column.id,
        kind: option.kind,
        metric: option.value,
        label: option.label,
      });
    }
  };

  const selectedOption = (
    column: TableColumnConfig
  ): ColumnSelectOption | null => {
    const value =
      column.kind === 'dimension' ? column.dimension : column.metric;

    if (column.kind === 'dimension') {
      return dimensionGroup.options.find((opt) => opt.value === value) ?? null;
    }
    return (
      metricGroupOptions
        .flatMap((group) => group.options)
        .find((option) => option.value === value) ?? null
    );
  };

  const headerLabel = (column: TableColumnConfig): string => {
    if (column.label) return column.label;
    if (column.kind === 'dimension') {
      return getColumnDimensionLabel(column.dimension);
    }
    return metricLabel(column.metric);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = columns.map((c) => c.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    const columnIds = arrayMove(ids, oldIndex, newIndex);
    dispatch({ type: 'REORDER_TABLE_COLUMNS', payload: { id, columnIds } });
  };

  return (
    <div className={layoutStyles.section}>
      <Text
        className={labelStyles.sectionLabel}
        fontSize="sm"
        fontWeight={600}
        mb="0.5rem"
      >
        Columns
      </Text>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={columns.map((column) => column.id)}
          strategy={verticalListSortingStrategy}
        >
          <Accordion allowMultiple>
            {columns.map((column) => (
              <SortableColumnItem
                key={column.id}
                column={column}
                isDisabled={isDisabled}
                columnSelectGroups={columnSelectGroups}
                selectedOption={selectedOption}
                onSelect={handleSelect}
                onUpdate={updateColumn}
                onRemove={removeColumn}
                headerLabel={headerLabel}
              />
            ))}
          </Accordion>
        </SortableContext>
      </DndContext>

      <RevelioSelect<ColumnSelectOption, false, GroupBase<ColumnSelectOption>>
        options={columnSelectGroups}
        value={null}
        placeholder="Add Column"
        size="sm"
        isDisabled={isDisabled}
        isSearchable
        isClearable={false}
        controlShouldRenderValue={false}
        onChange={(opt: SingleValue<ColumnSelectOption>) => {
          if (!opt) return;
          addColumn(opt);
        }}
      />
    </div>
  );
};
